import React, { useMemo, useState, Component } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CopyIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, ReceiptIcon, ActivityIcon, LandmarkIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { bankName, timeZoneAbbr } from '../../../shared/config/localeProfile';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
import { getPayoutById, getAccountDetails, getPayoutBreakdown, getDisplayStatus, getPayoutTraceId, getInitiatedAt, type Payout, type DisplayStatus, type BreakdownRow, type AdjustmentLine } from '../mocks/payoutsData';
// ── Formatting helpers ────────────────────────────────────────────────────
// All dates on this page are rendered in the tenant timezone (MVT = UTC+5)
// with the timezone explicit so merchants can reconcile against bank
// statements without having to guess.
const fmtAmount = (n: number) => formatCurrencyAmount(Math.abs(n), {
  fractionDigits: 2
});
const fmtSigned = (n: number) => `${n >= 0 ? '+' : '−'}${fmtAmount(n)}`;
// Full date + time with explicit MVT timezone, for the header and any
// "this is *the* moment that happened" context.
const fmtDateTimeMVT = (d: Date) => {
  const s = d.toLocaleString('en-GB', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${s} ${timeZoneAbbr()}`;
};
// Used inside expandable cards (e.g. account "Created") where the
// timezone is implicit from page context.
const fmtDateTime = (d: Date) => d.toLocaleString('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});
// ── Status pill ──────────────────────────────────────────────────────────
// "Sent" rather than "Paid" — we know we handed the payout to the bank,
// we don't know that the destination account has been credited. Blue
// (rather than green) signals "in motion, not yet final from your side".
const STATUS_LABEL: Record<DisplayStatus, string> = {
  paid: 'Sent',
  scheduled: 'Scheduled',
  partially_submitted: 'Partially submitted',
  failed: 'Failed',
  cancelled: 'Cancelled'
};
const STATUS_PILL: Record<DisplayStatus, string> = {
  paid: 'bg-blue-50 text-blue-700 border-blue-200',
  scheduled: 'bg-gray-50 text-gray-700 border-gray-200',
  partially_submitted: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-600 border-gray-200'
};
function StatusPill({
  status
}: {
  status: DisplayStatus;
}) {
  return <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded-full ${STATUS_PILL[status]}`} data-id="element-3415">
      {STATUS_LABEL[status]}
    </span>;
}
// ── Section primitive ────────────────────────────────────────────────────
function Section({
  icon: Icon,
  title,
  description,
  children,
  action
}: {
  icon?: ComponentType<{
    size?: number;
    className?: string;
  }>;
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return <section className="bg-white border border-gray-200 rounded-lg" data-id="element-3416">
      <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200" data-id="element-3417">
        <div className="min-w-0" data-id="element-3418">
          <div className="flex items-center gap-2" data-id="element-3419">
            {Icon && <Icon size={15} className="text-gray-500" aria-hidden="true" data-id="element-3420" />}
            <h2 className="text-sm font-semibold text-gray-900" data-id="element-3421">{title}</h2>
          </div>
          {description && <p className={`text-xs text-gray-500 mt-0.5 ${Icon ? 'ml-[22px]' : ''}`} data-id="element-3422">
              {description}
            </p>}
        </div>
        {action}
      </div>
      {children}
    </section>;
}
// ── Copy button ──────────────────────────────────────────────────────────
function CopyButton({
  value,
  label,
  size = 14
}: {
  value: string;
  label: string;
  size?: number;
}) {
  const [copied, setCopied] = useState(false);
  return <button type="button" onClick={async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }} className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors" aria-label={`Copy ${label}`} data-id="element-3423">
      {copied ? <CheckIcon size={size} className="text-emerald-600" aria-hidden="true" data-id="element-3424" /> : <CopyIcon size={size} aria-hidden="true" data-id="element-3425" />}
    </button>;
}
// ── Account details (expandable) ─────────────────────────────────────────
function AccountDetails({
  payout
}: {
  payout: Payout;
}) {
  const [open, setOpen] = useState(false);
  const a = getAccountDetails(payout);
  return <Section icon={LandmarkIcon} title="Account details" data-id="element-3426">
      <div data-id="element-3427">
        <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-4 px-6 py-4 hover:bg-gray-50 transition-colors" aria-expanded={open} data-id="element-3428">
          <span className="flex items-center gap-3 min-w-0" data-id="element-3429">
            {open ? <ChevronDownIcon size={16} className="text-gray-400 flex-shrink-0" aria-hidden="true" data-id="element-3430" /> : <ChevronRightIcon size={16} className="text-gray-400 flex-shrink-0" aria-hidden="true" data-id="element-3431" />}
            <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-gray-100 flex-shrink-0" data-id="element-3432">
              <LandmarkIcon size={16} className="text-gray-600" aria-hidden="true" data-id="element-3433" />
            </span>
            <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-3434">
              {a.bank}
            </span>
            <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-3435">
              •••• {a.last4}
            </span>
          </span>
          <span className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap" data-id="element-3436">
            <span aria-hidden="true" data-id="element-3437">{a.origin.flag}</span>
            {payout.currency}
          </span>
        </button>
        {open && <div className="px-6 pb-5 pt-1 border-t border-gray-100" data-id="element-3438">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 mt-4" data-id="element-3439">
              <KV label="Type" data-id="element-3440">{a.type}</KV>
              <KV label="Created" data-id="element-3441">{fmtDateTime(a.created)}</KV>
              <KV label="Account holder" data-id="element-3442">{a.accountHolder}</KV>
              <KV label="Origin" data-id="element-3443">
                <span className="inline-flex items-center gap-1.5" data-id="element-3444">
                  <span aria-hidden="true" data-id="element-3445">{a.origin.flag}</span>
                  {a.origin.country}
                </span>
              </KV>
              <KV label="Fingerprint" mono data-id="element-3446">
                {a.fingerprint}
              </KV>
              <KV label="Routing number" mono data-id="element-3447">
                {a.routingNumber}
              </KV>
              <KV label="ID" mono copyable={a.bankAccountId} data-id="element-3448">
                {a.bankAccountId}
              </KV>
            </dl>
          </div>}
      </div>
    </Section>;
}
function KV({
  label,
  children,
  mono,
  copyable
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}) {
  return <div className="grid grid-cols-[8rem_minmax(0,1fr)] items-start gap-3 min-w-0" data-id="element-3449">
      <dt className="text-xs text-gray-500 pt-0.5" data-id="element-3450">{label}</dt>
      <dd className={`flex items-center gap-1 text-sm text-gray-900 min-w-0 ${mono ? 'font-mono' : ''}`} data-id="element-3451">
        <span className="truncate min-w-0" data-id="element-3452">{children}</span>
        {copyable && <CopyButton value={copyable} label={label} size={12} data-id="element-3453" />}
      </dd>
    </div>;
}
// ── Summary table ────────────────────────────────────────────────────────
function SummarySection({
  payout
}: {
  payout: Payout;
}) {
  const b = getPayoutBreakdown(payout);
  const rows: Array<{
    label: string;
    row: BreakdownRow;
  }> = [{
    label: 'Payments',
    row: b.charges
  }, {
    label: 'Refunds',
    row: b.refunds
  }, {
    label: 'Chargebacks',
    row: b.chargebacks
  }].filter(r => r.row.count > 0 || r.row.gross !== 0);
  return <Section icon={ReceiptIcon} title="Summary" data-id="element-3454">
      <div className="px-6 pt-3 pb-4 overflow-x-auto" data-id="element-3455">
        <table className="w-full text-sm" data-id="element-3456">
          <thead data-id="element-3457">
            <tr className="text-xs text-gray-500" data-id="element-3458">
              <th className="text-left font-medium py-2 pr-4" data-id="element-3459">Type</th>
              <th className="text-right font-medium py-2 px-3 w-20" data-id="element-3460">Count</th>
              <th className="text-right font-medium py-2 px-3" data-id="element-3461">Gross</th>
              <th className="text-right font-medium py-2 px-3" data-id="element-3462">Fees</th>
              <th className="text-right font-medium py-2 pl-3" data-id="element-3463">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-3464">
            {rows.map(({
            label,
            row
          }) => <tr key={label} data-id="element-3465">
                <td className="py-2.5 pr-4 text-gray-900" data-id="element-3466">{label}</td>
                <td className="py-2.5 px-3 text-right tabular-nums text-gray-700" data-id="element-3467">
                  {row.count}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums text-gray-900" data-id="element-3468">
                  {fmtSigned(row.gross)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums text-gray-700" data-id="element-3469">
                  {row.fees === 0 ? '—' : fmtSigned(row.fees)}
                </td>
                <td className="py-2.5 pl-3 text-right tabular-nums text-gray-900 font-medium" data-id="element-3470">
                  {fmtSigned(row.total)}
                </td>
              </tr>)}
            {b.adjustments.map(a => <AdjustmentRow key={a.kind + a.reference} adjustment={a} data-id="element-3471" />)}
          </tbody>
          <tfoot data-id="element-3472">
            <tr className="border-t-2 border-gray-200" data-id="element-3473">
              <td className="pt-3 pr-4 text-sm font-semibold text-gray-900" data-id="element-3474">
                Net payout
              </td>
              <td data-id="element-3475" />
              <td data-id="element-3476" />
              <td data-id="element-3477" />
              <td className="pt-3 pl-3 text-right tabular-nums text-gray-900 font-semibold" data-id="element-3478">
                {fmtSigned(b.net)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Section>;
}
// Normalize a string to a comparable slug so we can detect when an
// adjustment's `reference` is just a slugified form of its `label` — in
// which case showing both is pure noise ("Payout fee" / "payout_fee").
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
function AdjustmentRow({
  adjustment
}: {
  adjustment: AdjustmentLine;
}) {
  const referenceIsRedundant = slugify(adjustment.reference) === slugify(adjustment.label);
  return <tr className="group" data-id="element-3479">
      <td className="py-2.5 pr-4" data-id="element-3480">
        <div className="text-gray-900" data-id="element-3481">{adjustment.label}</div>
        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap" data-id="element-3482">
          {!referenceIsRedundant && <>
              <span className="font-mono" data-id="element-3483">{adjustment.reference}</span>
              <span aria-hidden="true" className="text-gray-300" data-id="element-3484">
                ·
              </span>
            </>}
          <span data-id="element-3485">{adjustment.description}</span>
        </div>
      </td>
      <td className="py-2.5 px-3 text-right text-gray-400" data-id="element-3486">—</td>
      <td className="py-2.5 px-3 text-right text-gray-400" data-id="element-3487">—</td>
      <td className="py-2.5 px-3 text-right text-gray-400" data-id="element-3488">—</td>
      <td className={`py-2.5 pl-3 text-right tabular-nums font-medium ${adjustment.amount < 0 ? 'text-gray-700' : 'text-gray-900'}`} data-id="element-3489">
        {fmtSigned(adjustment.amount)}
      </td>
    </tr>;
}
// ── Right-rail Details ───────────────────────────────────────────────────
function DetailsRail({
  payout
}: {
  payout: Payout;
}) {
  const breakdown = useMemo(() => getPayoutBreakdown(payout), [payout]);
  // The page header already carries the date for this payout, so we don't
  // duplicate it in the rail. The rail is reference data only — IDs,
  // amounts, and the trace ID merchants reconcile against statements.
  const traceId = getPayoutTraceId(payout);
  const totalFees = breakdown.charges.fees + Math.abs(breakdown.chargebacks.fees);
  return <aside className="space-y-6" data-id="element-3490">
      <Section title="Details" data-id="element-3491">
        <dl className="px-6 py-4 space-y-4" data-id="element-3492">
          <DetailItem label="Payout ID" mono copyable={payout.payoutId} data-id="element-3493">
            {payout.payoutId}
          </DetailItem>
          <DetailItem label="Internal note" data-id="element-3494">{`${bankName().toUpperCase()} PAYOUT`}</DetailItem>
          <DetailItem label={<span className="inline-flex items-center" data-id="element-3496">
                <span data-id="element-3497">Payout trace ID</span>
                <InfoTooltip content='Your bank might ask you for this ID if you contact them about a payout. Keep in mind that some banks call this a "reference number" or something similar.' label="What is the payout trace ID?" placement="top" align="left" data-id="element-3498" />
              </span>} mono copyable={traceId} data-id="element-3495">
            {traceId}
          </DetailItem>
          <DetailItem label="Fee" data-id="element-3499">{fmtAmount(totalFees)}</DetailItem>
          <DetailItem label="Amount" data-id="element-3500">
            {fmtAmount(payout.grossAmount)}
          </DetailItem>
        </dl>
      </Section>

      <Section title="Metadata" data-id="element-3501">
        <div className="px-6 py-6 border-2 border-dashed border-gray-200 m-4 rounded text-center text-sm text-gray-400" data-id="element-3502">
          No metadata
        </div>
      </Section>
    </aside>;
}
function DetailItem({
  label,
  children,
  mono,
  copyable
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}) {
  return <div data-id="element-3503">
      <dt className="text-xs font-medium text-gray-500 mb-1" data-id="element-3504">{label}</dt>
      <dd className="flex items-center gap-1.5 min-w-0" data-id="element-3505">
        <span className={`text-sm text-gray-900 min-w-0 truncate ${mono ? 'font-mono' : ''}`} data-id="element-3506">
          {children}
        </span>
        {copyable && <CopyButton value={copyable} label={typeof label === 'string' ? label : 'value'} size={12} data-id="element-3507" />}
      </dd>
    </div>;
}
// ── Transfers list ───────────────────────────────────────────────────────
function TransfersSection({
  payout
}: {
  payout: Payout;
}) {
  if (payout.transfers.length <= 1) return null;
  return <Section icon={ActivityIcon} title="Bank transfers" description={`This payout was sent to ${payout.destinationAccount} as ${payout.transfers.length} separate transfers.`} data-id="element-3508">
      <div className="px-6 pt-3 pb-4 overflow-x-auto" data-id="element-3509">
        <table className="w-full text-sm" data-id="element-3510">
          <thead data-id="element-3511">
            <tr className="text-xs text-gray-500 border-b border-gray-100" data-id="element-3512">
              <th className="text-left font-medium py-2 pr-4" data-id="element-3513">Transfer ID</th>
              <th className="text-left font-medium py-2 px-3" data-id="element-3514">Status</th>
              <th className="text-left font-medium py-2 px-3" data-id="element-3515">
                <span className="inline-flex items-center" data-id="element-3516">
                  <span data-id="element-3517">Bank reference</span>
                  <InfoTooltip content="The reference assigned by Bank Rakyat for each individual transfer. Quote this number if you contact the bank about a specific transfer in this payout — it may appear on your bank statement." label="What is the bank reference?" placement="top" align="left" data-id="element-3518" />
                </span>
              </th>
              <th className="text-right font-medium py-2 pl-3" data-id="element-3519">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-3520">
            {payout.transfers.map(t => <tr key={t.transferId} className="align-top" data-id="element-3521">
                <td className="py-3 pr-4" data-id="element-3522">
                  <div className="font-mono text-gray-900" data-id="element-3523">{t.transferId}</div>
                  {t.description && <div className="text-xs text-gray-500 mt-0.5" data-id="element-3524">
                      {t.description}
                    </div>}
                </td>
                <td className="py-3 px-3" data-id="element-3525">
                  <TransferStatusBadge status={t.status} data-id="element-3526" />
                </td>
                <td className="py-3 px-3" data-id="element-3527">
                  <div className="flex items-center gap-1.5 min-w-0" data-id="element-3528">
                    <span className="font-mono text-gray-700 truncate" data-id="element-3529">
                      {t.bankReference}
                    </span>
                    <CopyButton value={t.bankReference} label="bank reference" size={12} data-id="element-3530" />
                  </div>
                </td>
                <td className="py-3 pl-3 text-right tabular-nums font-medium text-gray-900 whitespace-nowrap" data-id="element-3531">
                  {fmtAmount(t.netAmount)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </Section>;
}
function TransferStatusBadge({
  status
}: {
  status: 'submitted' | 'failed' | 'cancelled' | 'pending';
}) {
  const styles: Record<typeof status, string> = {
    submitted: 'bg-blue-50 text-blue-700 border-blue-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-gray-50 text-gray-600 border-gray-200',
    pending: 'bg-gray-50 text-gray-700 border-gray-200'
  };
  const label = status === 'submitted' ? 'Submitted' : status === 'failed' ? 'Failed' : status === 'cancelled' ? 'Cancelled' : 'Pending';
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${styles[status]}`} data-id="element-3532">
      {label}
    </span>;
}
// ── Page ─────────────────────────────────────────────────────────────────
export function PayoutDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const payout = id ? getPayoutById(id) : undefined;
  if (!payout) {
    return <div className="max-w-4xl mx-auto py-16 text-center" data-id="element-3533">
        <p className="text-gray-600 mb-4" data-id="element-3534">Payout not found.</p>
        <Link to="/payouts" className="text-sm font-medium text-yellow-700 hover:text-yellow-800" data-id="element-3535">
          ← Back to Payouts
        </Link>
      </div>;
  }
  const display = getDisplayStatus(payout);
  // The subtitle carries the single canonical date for this payout, with
  // an explicit MVT timestamp so it's unambiguous and reconcilable. For
  // sent payouts we surface the *initiation* moment (which we control)
  // rather than a "funds arrived" prediction we don't.
  const subtitle = (() => {
    const eff = payout.effectiveDateRaw;
    switch (display) {
      case 'paid':
        return `Sent to ${payout.destinationAccount} · initiated ${fmtDateTimeMVT(getInitiatedAt(payout))}`;
      case 'scheduled':
        return `Scheduled for ${fmtDateTimeMVT(eff)}`;
      case 'failed':
        return `Failed on ${fmtDateTimeMVT(eff)}`;
      case 'partially_submitted':
        return `Partially submitted on ${fmtDateTimeMVT(eff)}`;
      case 'cancelled':
        return `Cancelled on ${fmtDateTimeMVT(eff)}`;
    }
  })();
  return <div className="max-w-[1280px] mx-auto pb-12" data-id="element-3536">
      {/* Back link */}
      <button type="button" onClick={() => navigate('/payouts')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 rounded" data-id="element-3537">
        <ArrowLeftIcon size={14} aria-hidden="true" data-id="element-3538" />
        Payouts
      </button>

      {/* Header */}
      <div className="mb-6" data-id="element-3539">
        <div className="text-xs font-semibold uppercase tracking-wider text-yellow-700 mb-2" data-id="element-3540">
          Payouts
        </div>
        <div className="flex items-baseline gap-3 flex-wrap mb-2" data-id="element-3541">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900" data-id="element-3542">
            {fmtAmount(payout.grossAmount)}
          </h1>
          <span className="text-xl text-gray-500 font-medium" data-id="element-3543">
            {payout.currency}
          </span>
          <StatusPill status={display} data-id="element-3544" />
        </div>
        <p className="text-sm text-gray-600" data-id="element-3545">{subtitle}</p>
      </div>

      {/* Body: 2-col on desktop, stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-id="element-3546">
        <div className="space-y-6 min-w-0" data-id="element-3547">
          <AccountDetails payout={payout} data-id="element-3548" />

          <SummarySection payout={payout} data-id="element-3549" />

          <TransfersSection payout={payout} data-id="element-3550" />
        </div>

        <DetailsRail payout={payout} data-id="element-3551" />
      </div>
    </div>;
}